/**
 * Governance Audit Trail Page - Sprint 17
 * GC-9: Compliance export tracking and verification
 * 
 * Features:
 * - Table listing all compliance exports
 * - Filtering by date, user, project, portfolio
 * - View manifest, download archive, verify integrity
 * - Compare two exports side by side
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Download, Shield, Copy, Check, Eye, RefreshCw,
  Filter, Calendar, User, AlertTriangle, CheckCircle, XCircle,
  Loader2, ChevronLeft, ChevronRight, X, GitCompare
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Format bytes to human readable
const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });
};

// Filter Hash Tooltip Component
const FilterHashTooltip = ({ filters, filterHash }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(filterHash);
    setCopied(true);
    toast.success('Filter hash copied');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const filterSummary = Object.entries(filters || {})
    .filter(([_, v]) => v)
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ') || 'No filters';
  
  return (
    <div className="group relative">
      <span className="font-mono text-xs text-gray-500 cursor-pointer hover:text-gray-700">
        {filterHash ? filterHash.substring(0, 12) + '...' : '-'}
      </span>
      
      <div className="absolute z-50 invisible group-hover:visible bg-gray-900 text-white text-xs rounded-lg p-3 w-64 left-0 top-full mt-1 shadow-lg">
        <div className="font-medium mb-1">Filter Hash:</div>
        <div className="font-mono text-xs text-gray-300 mb-2 break-all">{filterHash}</div>
        <div className="text-gray-400 text-xs mb-2">{filterSummary}</div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-blue-300 hover:text-blue-200"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied!' : 'Copy hash'}
        </button>
      </div>
    </div>
  );
};

// Manifest Viewer Modal
const ManifestModal = ({ isOpen, onClose, exportId }) => {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && exportId) {
      setLoading(true);
      fetch(`${API_BASE}/governance/exports/${exportId}/manifest`, {
        headers: getAuthHeaders()
      })
        .then(res => res.json())
        .then(data => {
          setManifest(data.manifest);
          setLoading(false);
        })
        .catch(err => {
          toast.error('Failed to load manifest');
          setLoading(false);
        });
    }
  }, [isOpen, exportId]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Export Manifest</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : manifest ? (
          <div className="flex-1 overflow-auto">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Generated:</span>
                  <span className="ml-2 font-medium">{formatDate(manifest.generated_at)}</span>
                </div>
                <div>
                  <span className="text-gray-500">By:</span>
                  <span className="ml-2 font-medium">{manifest.generated_by}</span>
                </div>
                <div>
                  <span className="text-gray-500">Format:</span>
                  <span className="ml-2 font-medium uppercase">{manifest.format}</span>
                </div>
                <div>
                  <span className="text-gray-500">Mode:</span>
                  <span className="ml-2 font-medium capitalize">{manifest.mode}</span>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <h3 className="font-medium text-gray-700 mb-2">Archive Checksum (SHA-256)</h3>
              <div className="bg-gray-100 rounded p-2 font-mono text-xs break-all">
                {manifest.archive_checksum}
              </div>
            </div>
            
            {manifest.files && manifest.files.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Files ({manifest.files.length})</h3>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Filename</th>
                        <th className="px-3 py-2 text-right">Size</th>
                        <th className="px-3 py-2 text-left">SHA-256</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {manifest.files.map((file, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-xs">{file.filename}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{formatBytes(file.size_bytes)}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-500">{file.sha256?.substring(0, 16)}...</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No manifest available</p>
        )}
      </div>
    </div>
  );
};

// Compare Modal
const CompareModal = ({ isOpen, onClose, export1, export2 }) => {
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && export1 && export2) {
      setLoading(true);
      fetch(`${API_BASE}/governance/exports/compare?export_id_1=${export1.export_id}&export_id_2=${export2.export_id}`, {
        method: 'POST',
        headers: getAuthHeaders()
      })
        .then(res => res.json())
        .then(data => {
          setComparison(data);
          setLoading(false);
        })
        .catch(err => {
          toast.error('Failed to compare exports');
          setLoading(false);
        });
    }
  }, [isOpen, export1, export2]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <GitCompare className="w-5 h-5" />
            Compare Exports
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : comparison ? (
          <div className="flex-1 overflow-auto">
            {comparison.warning && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <div className="text-sm text-amber-800">{comparison.warning}</div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-medium text-blue-800 mb-2">Export 1</h3>
                <div className="text-sm space-y-1">
                  <div><span className="text-gray-500">Date:</span> {formatDate(comparison.export_1?.generated_at)}</div>
                  <div><span className="text-gray-500">User:</span> {comparison.export_1?.user?.name}</div>
                  <div><span className="text-gray-500">Files:</span> {comparison.export_1?.file_count}</div>
                  <div><span className="text-gray-500">Size:</span> {formatBytes(comparison.export_1?.total_bytes)}</div>
                </div>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="font-medium text-purple-800 mb-2">Export 2</h3>
                <div className="text-sm space-y-1">
                  <div><span className="text-gray-500">Date:</span> {formatDate(comparison.export_2?.generated_at)}</div>
                  <div><span className="text-gray-500">User:</span> {comparison.export_2?.user?.name}</div>
                  <div><span className="text-gray-500">Files:</span> {comparison.export_2?.file_count}</div>
                  <div><span className="text-gray-500">Size:</span> {formatBytes(comparison.export_2?.total_bytes)}</div>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <h3 className="font-medium text-gray-700 mb-2">Filter Hash Comparison</h3>
              <div className={`p-3 rounded-lg flex items-center gap-2 ${
                comparison.filter_hash_match ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {comparison.filter_hash_match ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <XCircle className="w-5 h-5" />
                )}
                <span>{comparison.filter_hash_match ? 'Filter hashes match' : 'Filter hashes differ'}</span>
              </div>
              
              {comparison.filter_differences?.length > 0 && (
                <ul className="mt-2 text-sm text-gray-600 space-y-1">
                  {comparison.filter_differences.map((diff, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="text-amber-500">*</span>
                      {diff}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Summary Deltas</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 rounded p-3">
                  <span className="text-gray-500">File Count Delta:</span>
                  <span className={`ml-2 font-medium ${
                    comparison.summary_deltas?.file_count_delta > 0 ? 'text-green-600' :
                    comparison.summary_deltas?.file_count_delta < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {comparison.summary_deltas?.file_count_delta > 0 ? '+' : ''}
                    {comparison.summary_deltas?.file_count_delta || 0}
                  </span>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <span className="text-gray-500">Size Delta:</span>
                  <span className={`ml-2 font-medium ${
                    comparison.summary_deltas?.total_bytes_delta > 0 ? 'text-green-600' :
                    comparison.summary_deltas?.total_bytes_delta < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {comparison.summary_deltas?.total_bytes_delta > 0 ? '+' : ''}
                    {formatBytes(Math.abs(comparison.summary_deltas?.total_bytes_delta || 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">Select two exports to compare</p>
        )}
      </div>
    </div>
  );
};

// Verify Modal
const VerifyModal = ({ isOpen, onClose, exportId }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  
  const handleVerify = async () => {
    setLoading(true);
    
    try {
      let res;
      if (file) {
        // Verify uploaded file
        const formData = new FormData();
        formData.append('file', file);
        
        res = await fetch(`${API_BASE}/governance/exports/verify`, {
          method: 'POST',
          headers: { ...getAuthHeaders() },
          body: formData
        });
      } else {
        // Verify by export ID
        res = await fetch(`${API_BASE}/governance/exports/verify`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ export_id: exportId })
        });
      }
      
      const data = await res.json();
      setResult(data);
    } catch (err) {
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Verify Export Integrity
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload archive to verify (optional)
          </label>
          <input
            type="file"
            accept=".zip,.html"
            onChange={(e) => setFile(e.target.files[0])}
            className="w-full border rounded-lg p-2 text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            Or verify using stored export record
          </p>
        </div>
        
        <button
          onClick={handleVerify}
          disabled={loading}
          className="w-full py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
          Verify
        </button>
        
        {result && (
          <div className={`mt-4 p-4 rounded-lg ${
            result.status === 'pass' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {result.status === 'pass' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              <span className={`font-medium ${result.status === 'pass' ? 'text-green-800' : 'text-red-800'}`}>
                Verification {result.status === 'pass' ? 'Passed' : 'Failed'}
              </span>
            </div>
            
            <div className="text-sm space-y-1">
              <div>Files checked: {result.checked_files}</div>
              <div>Files passed: {result.passed_files}</div>
              {result.details?.message && (
                <div className="text-gray-600">{result.details.message}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Page Component
const GovernanceAuditTrail = () => {
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [formatFilter, setFormatFilter] = useState('');
  const [users, setUsers] = useState([]);
  
  // Selection for compare
  const [selectedExports, setSelectedExports] = useState([]);
  
  // Modals
  const [manifestModal, setManifestModal] = useState({ open: false, exportId: null });
  const [verifyModal, setVerifyModal] = useState({ open: false, exportId: null });
  const [compareModal, setCompareModal] = useState(false);
  
  // Load exports
  const loadExports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (userFilter) params.append('userId', userFilter);
      if (formatFilter) params.append('format', formatFilter);
      params.append('limit', pagination.limit);
      params.append('offset', pagination.offset);
      
      const res = await fetch(`${API_BASE}/governance/exports?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setExports(data.exports || []);
        setPagination(p => ({ ...p, total: data.pagination?.total || 0 }));
      }
    } catch (err) {
      toast.error('Failed to load exports');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, userFilter, formatFilter, pagination.limit, pagination.offset]);
  
  // Load users for filter
  useEffect(() => {
    fetch(`${API_BASE}/governance/exports/users`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => setUsers(data.users || []))
      .catch(() => {});
  }, []);
  
  useEffect(() => {
    loadExports();
  }, [loadExports]);
  
  // Handle selection for compare
  const handleSelect = (exp) => {
    if (selectedExports.find(e => e.export_id === exp.export_id)) {
      setSelectedExports(selectedExports.filter(e => e.export_id !== exp.export_id));
    } else if (selectedExports.length < 2) {
      setSelectedExports([...selectedExports, exp]);
    } else {
      toast.info('You can select up to 2 exports to compare');
    }
  };
  
  // Handle download
  const handleDownload = async (exportId, filterHash) => {
    try {
      const res = await fetch(`${API_BASE}/governance/exports/${exportId}/download`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compliance_pack_${filterHash}.zip`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Download failed');
      }
    } catch (err) {
      toast.error('Download failed');
    }
  };
  
  // Clear filters
  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setUserFilter('');
    setFormatFilter('');
    setPagination(p => ({ ...p, offset: 0 }));
  };
  
  return (
    <div className="min-h-screen bg-gray-100 p-6" data-testid="audit-trail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance Audit Trail</h1>
          <p className="text-gray-500">Track and verify governance compliance pack exports</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadExports}
            className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-white"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {selectedExports.length === 2 && (
            <button
              onClick={() => setCompareModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              data-testid="compare-exports-btn"
            >
              <GitCompare className="w-4 h-4" />
              Compare Selected
            </button>
          )}
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg p-4 mb-6 border">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-gray-700">Filters</span>
        </div>
        
        <div className="grid grid-cols-5 gap-4">
          <div>
            <label className="text-xs text-gray-500">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">User</label>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="w-full border rounded px-2 py-1.5 text-sm"
            >
              <option value="">All Users</option>
              {users.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500">Format</label>
            <select
              value={formatFilter}
              onChange={(e) => setFormatFilter(e.target.value)}
              className="w-full border rounded px-2 py-1.5 text-sm"
            >
              <option value="">All Formats</option>
              <option value="zip">ZIP</option>
              <option value="pdf">PDF</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      
      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : exports.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No compliance exports found</p>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Select</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date (UTC)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filters</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branding</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Checksum</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {exports.map((exp) => {
                  const isSelected = selectedExports.find(e => e.export_id === exp.export_id);
                  
                  return (
                    <tr key={exp.export_id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={!!isSelected}
                          onChange={() => handleSelect(exp)}
                          className="w-4 h-4 rounded text-blue-600"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {formatDate(exp.generated_at)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span>{exp.user?.name || exp.user?.email || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <FilterHashTooltip filters={exp.filters} filterHash={exp.filter_hash} />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                          exp.format === 'zip' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {exp.format}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 capitalize">{exp.mode}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 capitalize">{exp.branding}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">
                        {exp.archive_checksum?.substring(0, 12)}...
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setManifestModal({ open: true, exportId: exp.export_id })}
                            className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
                            title="View Manifest"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownload(exp.export_id, exp.filter_hash)}
                            className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-blue-600"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setVerifyModal({ open: true, exportId: exp.export_id })}
                            className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-green-600"
                            title="Verify"
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Pagination */}
            <div className="px-4 py-3 border-t flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
                  disabled={pagination.offset === 0}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPagination(p => ({ ...p, offset: p.offset + p.limit }))}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Modals */}
      <ManifestModal
        isOpen={manifestModal.open}
        onClose={() => setManifestModal({ open: false, exportId: null })}
        exportId={manifestModal.exportId}
      />
      
      <VerifyModal
        isOpen={verifyModal.open}
        onClose={() => setVerifyModal({ open: false, exportId: null })}
        exportId={verifyModal.exportId}
      />
      
      <CompareModal
        isOpen={compareModal}
        onClose={() => setCompareModal(false)}
        export1={selectedExports[0]}
        export2={selectedExports[1]}
      />
    </div>
  );
};

export default GovernanceAuditTrail;
